/**
 * Enhanced ContextManager.js
 * Implements the Model Context Protocol (MCP) for managing state across different scopes
 * Enhanced with MCP server integration, real-time synchronization, and advanced features
 */

class ContextManager {
  constructor() {
    this.contexts = {
      global: {}, // Global context accessible to all
      flow: {}, // Context specific to current flow
      node: {}, // Node-specific context
      mcp: {}, // MCP server state and tool contexts
      session: {}, // User session state
    };

    this.metadata = {
      global: {},
      flow: {},
      node: {},
      mcp: {},
      session: {},
    };

    this.history = {
      global: [],
      flow: [],
      node: {},
      mcp: {},
      session: {},
    };

    this.maxHistoryLength = 100; // Increased history length
    this.subscribers = new Map(); // Event subscribers
    this.mcpServers = new Map(); // Connected MCP servers
    this.syncQueue = []; // Pending sync operations
    this.isSyncing = false; // Sync in progress flag

    // Initialize MCP integration
    this._initializeMCPIntegration();
  }

  /**
   * Initialize MCP integration features
   */
  _initializeMCPIntegration() {
    // MCP server state tracking
    this.mcpConnectionState = {
      connected: false,
      lastSync: null,
      serverInfo: null,
      capabilities: [],
    };

    // Context synchronization settings
    this.syncConfig = {
      autoSync: true,
      syncInterval: 5000, // 5 seconds
      maxRetries: 3,
      retryDelay: 1000,
    };

    // Start auto-sync if enabled
    if (this.syncConfig.autoSync) {
      this._startAutoSync();
    }
  }

  /**
   * Start automatic context synchronization
   */
  _startAutoSync() {
    setInterval(() => {
      if (this.mcpConnectionState.connected && this.syncQueue.length > 0) {
        this._processSyncQueue();
      }
    }, this.syncConfig.syncInterval);
  }

  /**
   * Get a context value with proper access control
   * @param {string} key - The state variable key
   * @param {string} scope - The scope of the variable (global, flow, node)
   * @param {string} nodeId - Optional node ID for node-scoped variables
   * @returns {any} The value or undefined if not found
   */
  get(key, scope = 'global', nodeId = null) {
    // Validate scope - valid scopes: global, flow, node, mcp, session
    const validScopes = ['global', 'flow', 'node', 'mcp', 'session'];
    if (!validScopes.includes(scope)) {
      console.error(`Invalid scope: ${scope}. Valid scopes: ${validScopes.join(', ')}`);
      return undefined;
    }

    if (!this.contexts[scope]) {
      console.warn(`Context scope '${scope}' not initialized, initializing...`);
      this.contexts[scope] = {};
    }

    // For node scope, we need a nodeId
    if (scope === 'node' && !nodeId) {
      console.error('Node ID is required for node scope');
      return undefined;
    }

    // Get the appropriate context
    const context = this._getContextForScope(scope, nodeId);

    // Check if the key exists in this scope
    if (context[key] !== undefined) {
      return context[key].value;
    }

    // If not found in current scope, try to inherit from parent scope
    if (scope === 'node') {
      return this.get(key, 'flow');
    } if (scope === 'flow') {
      return this.get(key, 'global');
    }

    return undefined;
  }

  /**
   * Set a context value with metadata
   * @param {string} key - The state variable key
   * @param {any} value - The value to store
   * @param {string} scope - The scope of the variable (global, flow, node)
   * @param {string} nodeId - Optional node ID for node-scoped variables
   * @param {object} options - Additional options (type, description, persistent, etc.)
   * @returns {boolean} Success or failure
   */
  set(key, value, scope = 'global', nodeId = null, options = {}) {
    // Validate scope
    if (!this.contexts[scope]) {
      console.error(`Invalid scope: ${scope}`);
      return false;
    }

    // For node scope, we need a nodeId
    if (scope === 'node' && !nodeId) {
      console.error('Node ID is required for node scope');
      return false;
    }

    // Get the appropriate context
    const context = this._getContextForScope(scope, nodeId);
    const metadata = this._getMetadataForScope(scope, nodeId);

    // Save the previous version to history
    if (context[key] !== undefined) {
      this._addToHistory(scope, nodeId, key, context[key]);
    }

    // Set the new value with metadata
    context[key] = {
      value,
      type: options.type || typeof value,
      updatedAt: new Date().toISOString(),
      updatedBy: options.updatedBy || 'system',
      description: options.description || '',
      persistent: options.persistent || false,
    };

    // Update metadata
    metadata[key] = {
      createdAt: metadata[key]?.createdAt || new Date().toISOString(),
      versions: (metadata[key]?.versions || 0) + 1,
    };

    return true;
  }

  /**
   * Check if a context variable exists
   * @param {string} key - The state variable key
   * @param {string} scope - The scope to check
   * @param {string} nodeId - Optional node ID
   * @param {boolean} checkParents - Whether to check parent scopes
   * @returns {boolean} Whether the variable exists
   */
  has(key, scope = 'global', nodeId = null, checkParents = true) {
    // Direct check in specified scope
    const context = this._getContextForScope(scope, nodeId);
    if (context[key] !== undefined) return true;

    // Check parent scopes if requested
    if (checkParents) {
      if (scope === 'node') {
        return this.has(key, 'flow', null, true);
      } if (scope === 'flow') {
        return this.has(key, 'global', null, true);
      }
    }

    return false;
  }

  /**
   * Delete a context variable
   * @param {string} key - The state variable key
   * @param {string} scope - The scope of the variable
   * @param {string} nodeId - Optional node ID
   * @returns {boolean} Success or failure
   */
  delete(key, scope = 'global', nodeId = null) {
    const context = this._getContextForScope(scope, nodeId);

    if (context[key] === undefined) {
      return false;
    }

    // Save to history before deleting
    this._addToHistory(scope, nodeId, key, context[key]);

    // Delete the variable
    delete context[key];
    return true;
  }

  /**
   * Get all context variables for a scope
   * @param {string} scope - The scope to get
   * @param {string} nodeId - Optional node ID
   * @returns {object} All context variables in this scope
   */
  getAll(scope = 'global', nodeId = null) {
    const context = this._getContextForScope(scope, nodeId);
    const result = {};

    // Extract just the values (not the metadata)
    // Add null check to prevent "Cannot convert undefined or null to object" error
    if (context && typeof context === 'object') {
      Object.entries(context).forEach(([key, data]) => {
        result[key] = data.value;
      });
    }

    return result;
  }

  /**
   * Get all context variables with full metadata
   * @param {string} scope - The scope to get
   * @param {string} nodeId - Optional node ID
   * @returns {object} All context variables with metadata
   */
  getAllWithMetadata(scope = 'global', nodeId = null) {
    if (scope === 'node' && nodeId) {
      // Always return an object, even if no vars exist for this node
      return this.contexts.node[nodeId] || {};
    }
    return this.contexts[scope] || {};
  }

  /**
   * Rollback a variable to a previous version
   * @param {string} key - The variable key
   * @param {string} scope - The scope
   * @param {string} nodeId - Optional node ID
   * @param {number} version - Version index to rollback to (0 = most recent)
   * @returns {boolean} Success or failure
   */
  rollback(key, scope = 'global', nodeId = null, version = 0) {
    const history = this._getHistoryForScope(scope, nodeId);

    if (!history[key] || !history[key][version]) {
      return false;
    }

    const prevVersion = history[key][version];
    const context = this._getContextForScope(scope, nodeId);

    // Save current state to history
    this._addToHistory(scope, nodeId, key, context[key]);

    // Restore previous version
    context[key] = { ...prevVersion, restoredAt: new Date().toISOString() };
    return true;
  }

  // Helper methods
  _getContextForScope(scope, nodeId) {
    if (scope === 'node' && nodeId) {
      // Ensure contexts.node exists
      if (!this.contexts.node) {
        this.contexts.node = {};
      }
      this.contexts.node[nodeId] = this.contexts.node[nodeId] || {};
      return this.contexts.node[nodeId];
    }
    return this.contexts[scope] || {};
  }

  _getMetadataForScope(scope, nodeId) {
    if (scope === 'node' && nodeId) {
      // Ensure metadata.node exists
      if (!this.metadata.node) {
        this.metadata.node = {};
      }
      this.metadata.node[nodeId] = this.metadata.node[nodeId] || {};
      return this.metadata.node[nodeId];
    }
    return this.metadata[scope] || {};
  }

  _getHistoryForScope(scope, nodeId) {
    if (scope === 'node' && nodeId) {
      // Ensure history.node exists
      if (!this.history.node) {
        this.history.node = {};
      }
      this.history.node[nodeId] = this.history.node[nodeId] || {};
      return this.history.node[nodeId];
    }
    return this.history[scope] || {};
  }

  _addToHistory(scope, nodeId, key, value) {
    const history = this._getHistoryForScope(scope, nodeId);

    // Initialize history array for this key if it doesn't exist
    history[key] = history[key] || [];

    // Add to history
    history[key].unshift({ ...value, archivedAt: new Date().toISOString() });

    // Limit history length
    if (history[key].length > this.maxHistoryLength) {
      history[key] = history[key].slice(0, this.maxHistoryLength);
    }
  }

  /**
   * MCP Server Integration Methods
   */

  /**
   * Connect to MCP server
   * @param {object} serverConfig - Server configuration
   */
  async connectToMCPServer(serverConfig) {
    try {
      this.mcpConnectionState.connected = true;
      this.mcpConnectionState.serverInfo = serverConfig;
      this.mcpConnectionState.lastSync = new Date().toISOString();

      // Initialize MCP context
      this.contexts.mcp[serverConfig.id] = {
        server: serverConfig,
        tools: {},
        resources: {},
        capabilities: [],
      };

      // Notify subscribers
      this._emit('mcp:connected', {
        serverId: serverConfig.id,
        timestamp: this.mcpConnectionState.lastSync,
      });

      console.log(`✅ Connected to MCP server: ${serverConfig.name}`);
      return true;
    } catch (error) {
      console.error('Failed to connect to MCP server:', error);
      this.mcpConnectionState.connected = false;
      throw error;
    }
  }

  /**
   * Disconnect from MCP server
   * @param {string} serverId - Server ID
   */
  async disconnectFromMCPServer(serverId) {
    try {
      if (this.contexts.mcp[serverId]) {
        delete this.contexts.mcp[serverId];
      }

      this.mcpConnectionState.connected = false;
      this.mcpConnectionState.lastSync = new Date().toISOString();

      // Notify subscribers
      this._emit('mcp:disconnected', {
        serverId,
        timestamp: this.mcpConnectionState.lastSync,
      });

      console.log(`🔌 Disconnected from MCP server: ${serverId}`);
      return true;
    } catch (error) {
      console.error('Failed to disconnect from MCP server:', error);
      throw error;
    }
  }

  /**
   * Register MCP tool in context
   * @param {string} serverId - Server ID
   * @param {object} tool - Tool definition
   */
  registerMCPTool(serverId, tool) {
    if (!this.contexts.mcp[serverId]) {
      this.contexts.mcp[serverId] = { tools: {}, resources: {}, capabilities: [] };
    }

    this.contexts.mcp[serverId].tools[tool.name] = {
      definition: tool,
      lastUsed: null,
      usageCount: 0,
      metadata: {},
    };

    this._emit('mcp:tool_registered', {
      serverId,
      toolName: tool.name,
      timestamp: new Date().toISOString(),
    });

    console.log(`🛠️ Registered MCP tool: ${tool.name}`);
  }

  /**
   * Execute MCP tool
   * @param {string} serverId - Server ID
   * @param {string} toolName - Tool name
   * @param {object} args - Tool arguments
   */
  async executeMCPTool(serverId, toolName, args) {
    try {
      const serverContext = this.contexts.mcp[serverId];
      if (!serverContext || !serverContext.tools[toolName]) {
        throw new Error(`Tool ${toolName} not found on server ${serverId}`);
      }

      const tool = serverContext.tools[toolName];

      // Track usage
      tool.lastUsed = new Date().toISOString();
      tool.usageCount += 1;

      // Create execution context
      const executionContext = {
        input: args,
        timestamp: tool.lastUsed,
        userId: this.get('userId', 'session') || 'anonymous',
        flowId: this.get('currentFlowId', 'flow'),
        nodeId: this.get('currentNodeId', 'node'),
      };

      // Store execution context
      this.set(`execution_${Date.now()}`, executionContext, 'mcp', serverId);

      this._emit('mcp:tool_executed', {
        serverId,
        toolName,
        executionId: executionContext.timestamp,
        timestamp: new Date().toISOString(),
      });

      console.log(`⚡ Executing MCP tool: ${toolName}`);
      return executionContext;
    } catch (error) {
      console.error('Failed to execute MCP tool:', error);
      throw error;
    }
  }

  /**
   * Synchronization Methods
   */

  /**
   * Sync context with MCP server
   * @param {string} scope - Scope to sync
   */
  async syncWithMCPServer(scope = 'all') {
    if (!this.mcpConnectionState.connected) {
      console.warn('Cannot sync: not connected to MCP server');
      return;
    }

    const syncOperation = {
      id: Date.now().toString(),
      scope,
      timestamp: new Date().toISOString(),
      data: scope === 'all' ? this.export() : this.exportScope(scope),
    };

    this.syncQueue.push(syncOperation);
    await this._processSyncQueue();

    return syncOperation.id;
  }

  /**
   * Process sync queue
   */
  async _processSyncQueue() {
    if (this.isSyncing || this.syncQueue.length === 0) {
      return;
    }

    this.isSyncing = true;

    try {
      while (this.syncQueue.length > 0) {
        const operation = this.syncQueue.shift();

        // Simulate MCP server sync
        await this._performSyncOperation(operation);

        this.mcpConnectionState.lastSync = new Date().toISOString();

        this._emit('mcp:sync_completed', {
          operationId: operation.id,
          timestamp: this.mcpConnectionState.lastSync,
        });
      }
    } catch (error) {
      console.error('Sync operation failed:', error);
      this._emit('mcp:sync_failed', { error: error.message });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Perform individual sync operation
   */
  async _performSyncOperation(operation) {
    // Simulate async sync with MCP server
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`🔄 Synced ${operation.scope} with MCP server`);
        resolve();
      }, 100);
    });
  }

  /**
   * Export specific scope
   */
  exportScope(scope) {
    return {
      contexts: { [scope]: this.contexts[scope] },
      metadata: { [scope]: this.metadata[scope] },
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Event System Methods
   */

  /**
   * Subscribe to context events
   * @param {string} event - Event name
   * @param {function} handler - Event handler
   */
  subscribe(event, handler) {
    if (!this.subscribers.has(event)) {
      this.subscribers.set(event, new Set());
    }
    this.subscribers.get(event).add(handler);
  }

  /**
   * Unsubscribe from context events
   * @param {string} event - Event name
   * @param {function} handler - Event handler
   */
  unsubscribe(event, handler) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).delete(handler);
    }
  }

  /**
   * Emit event to subscribers
   * @param {string} event - Event name
   * @param {object} data - Event data
   */
  _emit(event, data) {
    if (this.subscribers.has(event)) {
      this.subscribers.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Enhanced context access with MCP integration
   */

  /**
   * Get MCP server info
   */
  getMCPServerInfo() {
    return {
      connected: this.mcpConnectionState.connected,
      lastSync: this.mcpConnectionState.lastSync,
      serverInfo: this.mcpConnectionState.serverInfo,
      capabilities: this.mcpConnectionState.capabilities,
    };
  }

  /**
   * Get available MCP tools
   */
  getAvailableMCPTools(serverId = null) {
    const tools = {};

    Object.entries(this.contexts.mcp).forEach(([serverId, serverData]) => {
      if (!serverId) {
        return;
      }
      if (serverData && serverData.tools) {
        Object.entries(serverData.tools).forEach(([toolName, toolData]) => {
          tools[toolName] = {
            serverId,
            definition: toolData.definition,
            lastUsed: toolData.lastUsed,
            usageCount: toolData.usageCount,
          };
        });
      }
    });

    return tools;
  }

  /**
   * Get context statistics
   */
  getContextStats() {
    const stats = {
      scopes: {},
      mcp: {
        servers: Object.keys(this.contexts.mcp).length,
        tools: 0,
        resources: 0,
        syncQueue: this.syncQueue.length,
        lastSync: this.mcpConnectionState.lastSync,
      },
      events: {
        subscribers: Array.from(this.subscribers.entries()).reduce((sum, [, set]) => sum + set.size, 0),
      },
    };

    // Calculate stats per scope
    Object.keys(this.contexts).forEach((scope) => {
      const context = this.contexts[scope];
      const metadata = this.metadata[scope];

      if (typeof context === 'object') {
        stats.scopes[scope] = {
          variables: Object.keys(context).length,
          metadata: Object.keys(metadata).length,
          history: scope === 'node' ? 0 : this.history[scope].length,
        };
      }
    });

    // Count MCP tools and resources
    Object.values(this.contexts.mcp).forEach((server) => {
      stats.mcp.tools += Object.keys(server.tools || {}).length;
      stats.mcp.resources += Object.keys(server.resources || {}).length;
    });

    return stats;
  }

  /**
   * Export the entire context state for persistence
   * @returns {object} The serializable context state
   */
  export() {
    return {
      contexts: this.contexts,
      metadata: this.metadata,
      mcpConnectionState: this.mcpConnectionState,
      syncConfig: this.syncConfig,
      // We don't export history to keep the export size smaller
    };
  }

  /**
   * Import a previously exported context state
   * @param {object} data - The data to import
   */
  import(data) {
    // Add null check to prevent errors when importing invalid data
    if (!data || typeof data !== 'object') {
      console.warn('ContextManager: Invalid data provided to import method');
      return;
    }

    if (data.contexts && typeof data.contexts === 'object') {
      this.contexts = data.contexts;
    }

    if (data.metadata && typeof data.metadata === 'object') {
      this.metadata = data.metadata;
    }

    // Reset history since we don't export/import it
    this.history = {
      global: [],
      flow: [],
      node: {},
    };
  }
}

export default ContextManager;
